import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { AlertTriangle, Clock, MapPin, Users } from 'lucide-react';

const SERVICE_LABELS = {
  day_program: 'Explore Program',
  employment: 'Employment',
  in_home: 'In-Home & Community',
};

const SERVICE_COLORS = {
  day_program: 'bg-[#EDE7F6] text-[#4527A0] border-[#B39DDB]',
  employment: 'bg-[#E0F2F1] text-[#00695C] border-[#80CBC4]',
  in_home: 'bg-[#FFF3E0] text-[#E65100] border-[#FFB74D]',
};

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 || 12;
  return `${hr}:${String(m).padStart(2, '0')} ${period}`;
};

const fmtDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch { return d; }
};

export default function ConflictDialog({ conflict, onCancel, onConfirm, busy }) {
  const open = !!conflict;
  const c = conflict || {};
  const cs = c.conflicting_shift || {};
  const ns = c.new_shift || {};
  const empName = c.employee_name || 'This staff member';
  const sameLine = cs.service_line && ns.service_line && cs.service_line === ns.service_line;
  const isOverlap = c.type === 'overlap';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel?.(); }}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="shift-conflict-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <AlertTriangle className="h-5 w-5 text-[#F4A261]" />
            {isOverlap ? 'Double-booking detected' : 'Buffer conflict'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-[#2D1B4E]" data-testid="conflict-summary">
            <strong>{empName}</strong> {isOverlap ? 'is already scheduled' : 'has another shift within 15 minutes'} on <strong>{fmtDate(cs.date || ns.date)}</strong>
            {sameLine ? '.' : (cs.service_line ? <> in a different service line ({SERVICE_LABELS[cs.service_line] || cs.service_line}).</> : '.')}
          </p>

          <div className={`rounded-lg border p-3 space-y-2 ${SERVICE_COLORS[cs.service_line] || 'bg-[#F7F4EF] border-[#DED8EB] text-[#2D1B4E]'}`} data-testid="conflicting-shift-block">
            <div className="text-xs font-semibold uppercase opacity-70">Existing shift</div>
            <div className="font-semibold">{SERVICE_LABELS[cs.service_line] || cs.service_line || 'Shift'}{cs.group_number ? ` · Group ${cs.group_number}` : ''}</div>
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-3.5 w-3.5" />
              <span style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{fmtTime(cs.time_in)} – {fmtTime(cs.time_out)}</span>
            </div>
            {cs.location && <div className="flex items-center gap-2 text-sm"><MapPin className="h-3.5 w-3.5" /><span>{cs.location}</span></div>}
          </div>

          <div className="rounded-lg border border-dashed border-[#DED8EB] bg-white p-3 space-y-2">
            <div className="text-xs font-semibold uppercase text-[#8E85A0]">New shift you are creating</div>
            <div className="font-semibold text-[#2D1B4E]">{SERVICE_LABELS[ns.service_line] || ns.service_line || 'Shift'}{ns.group_number ? ` · Group ${ns.group_number}` : ''}</div>
            <div className="flex items-center gap-2 text-sm text-[#2D1B4E]">
              <Clock className="h-3.5 w-3.5" />
              <span style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{fmtTime(ns.time_in)} – {fmtTime(ns.time_out)}</span>
            </div>
          </div>

          <p className="text-xs text-[#8E85A0] flex items-start gap-1.5">
            <Users className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
              If the existing shift is wrong (e.g., a swap was never finalized), open the <strong>{SERVICE_LABELS[cs.service_line] || 'other'}</strong> tab and remove or reassign it before continuing.
            </span>
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} className="border-[#DED8EB]" data-testid="conflict-cancel-btn">Cancel</Button>
          <Button onClick={onConfirm} disabled={busy} className="bg-[#D9534F] hover:bg-[#C9302C] text-white" data-testid="conflict-confirm-btn">
            {busy ? 'Saving…' : 'Assign anyway'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
